import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import client, { fetcher } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download } from "lucide-react";
import "@/i18n/fase4";

export default function Auditoria() {
  const { t } = useTranslation();
  const [action, setAction] = useState("all");
  const [page, setPage] = useState(1);

  const qs = useMemo(() => {
    const p = new URLSearchParams({ limit: "50", page: String(page) });
    if (action !== "all") p.set("action", action);
    return p.toString();
  }, [action, page]);
  const { data } = useSWR(`/audit/log?${qs}`, fetcher);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (action !== "all") params.set("action", action);
      const res = await client.get(`/audit/export?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "audit-log.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Erro"); }
  };

  return (
    <div data-testid="auditoria-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-salnes-stone">{t("fase4.sidebar_auditoria")}</div>
          <h1 className="font-serif text-3xl text-salnes-ink">{t("fase4.aud_title")}</h1>
          <div className="text-sm text-salnes-stone mt-1">{t("fase4.aud_subtitle")}</div>
        </div>
        <div className="flex gap-2">
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="w-[240px]" data-testid="audit-filter-action"><SelectValue placeholder={t("fase4.aud_filter_action")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("fase4.aud_filter_all")}</SelectItem>
              {(data?.actions || []).map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCsv} data-testid="audit-export-btn" className="rounded-full">
            <Download size={13} className="mr-2" /> {t("fase4.aud_export")}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border hairline bg-white overflow-hidden">
        <div className="px-6 py-3 border-b hairline text-xs text-salnes-stone">
          {t("fase4.aud_total", { n: data?.total ?? 0 })}
        </div>
        <table className="w-full text-sm" data-testid="audit-log-table">
          <thead className="bg-salnes-sand/40 text-salnes-stone text-[10px] uppercase tracking-[0.18em]">
            <tr>
              <th className="text-left px-4 py-3">{t("fase4.aud_th_at")}</th>
              <th className="text-left px-4 py-3">{t("fase4.aud_th_user")}</th>
              <th className="text-left px-4 py-3">{t("fase4.aud_th_role")}</th>
              <th className="text-left px-4 py-3">{t("fase4.aud_th_action")}</th>
              <th className="text-left px-4 py-3">{t("fase4.aud_th_meta")}</th>
            </tr>
          </thead>
          <tbody className="divide-y hairline">
            {(data?.items || []).map((e, i) => (
              <tr key={i} data-testid={`audit-row-${i}`} className="hover:bg-salnes-sand/20">
                <td className="px-4 py-3 text-[12px] text-salnes-ink whitespace-nowrap tabular-nums">{e.at?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-3 text-[12px]">{e.user_id}</td>
                <td className="px-4 py-3"><Badge variant="outline" className="text-[9px]">{e.role}</Badge></td>
                <td className="px-4 py-3"><code className="text-[11px] bg-salnes-sand/40 px-2 py-0.5 rounded">{e.action}</code></td>
                <td className="px-4 py-3 text-[11px] text-salnes-stone">
                  <pre className="whitespace-pre-wrap max-w-[500px] font-mono text-[10px]">{JSON.stringify(e.meta, null, 0)}</pre>
                </td>
              </tr>
            ))}
            {(!data || data.items.length === 0) && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-salnes-stone text-sm italic">{t("fase4.aud_no_data")}</td></tr>
            )}
          </tbody>
        </table>
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t hairline text-xs text-salnes-stone">
            <div>página {data.page}/{data.pages}</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</Button>
              <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>›</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
